package org.teddapp.project.vault

import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

// Le chiffrement du trousseau dans le navigateur. WebCrypto ne répond qu'en promesses ;
// VaultCipher est synchrone. On passe donc par les bibliothèques noble (auditées, sans
// dépendance), avec exactement les réglages de la JVM et d'Android : PBKDF2-HMAC-SHA256
// pour la clé, AES-256-GCM avec un nonce de 12 octets placé devant, le tout en Base64.
// VaultCipherInteropTest garde la compatibilité, dans les deux sens.

@JsModule("@noble/hashes/pbkdf2")
@JsNonModule
private external object NoblePbkdf2 {
    fun pbkdf2(hash: dynamic, password: String, salt: Uint8Array, opts: dynamic): Uint8Array
}

@JsModule("@noble/hashes/sha256")
@JsNonModule
private external object NobleSha256 {
    val sha256: dynamic
}

@JsModule("@noble/ciphers/aes")
@JsNonModule
private external object NobleAes {
    fun gcm(key: Uint8Array, nonce: Uint8Array): dynamic
}

private fun ByteArray.u8(): Uint8Array {
    val i8 = unsafeCast<Int8Array>()
    return Uint8Array(i8.buffer, i8.byteOffset, i8.length)
}

private fun Uint8Array.bytes(): ByteArray = Int8Array(buffer, byteOffset, length).unsafeCast<ByteArray>().copyOf()

private fun randomBytes(n: Int): ByteArray =
    js("crypto.getRandomValues(new Uint8Array(n))").unsafeCast<Uint8Array>().bytes()

@OptIn(ExperimentalEncodingApi::class)
actual object VaultCipher {
    private const val NONCE_BYTES = 12

    private fun b64(b: ByteArray): String = Base64.encode(b)
    private fun raw(s: String): ByteArray = Base64.decode(s)

    actual val isAvailable: Boolean = true

    actual fun randomSalt(): String = b64(randomBytes(16))

    actual fun newKey(): String = b64(randomBytes(32))

    actual fun deriveKey(passphrase: String, salt: String, iterations: Int): String {
        val opts = js("({})")
        opts.c = iterations
        opts.dkLen = 32
        return b64(NoblePbkdf2.pbkdf2(NobleSha256.sha256, passphrase, raw(salt).u8(), opts).bytes())
    }

    actual fun seal(key: String, plaintext: String): String {
        val nonce = randomBytes(NONCE_BYTES)
        val sealed = NobleAes.gcm(raw(key).u8(), nonce.u8()).encrypt(plaintext.encodeToByteArray().u8()).unsafeCast<Uint8Array>()
        return b64(nonce + sealed.bytes())
    }

    actual fun open(key: String, sealed: String): String? = runCatching {
        val data = raw(sealed)
        val nonce = data.copyOfRange(0, NONCE_BYTES)
        val body = data.copyOfRange(NONCE_BYTES, data.size)
        NobleAes.gcm(raw(key).u8(), nonce.u8()).decrypt(body.u8()).unsafeCast<Uint8Array>().bytes().decodeToString()
    }.getOrNull()
}
